import { useMemo, useState } from 'react';
import { useListQrCodes, type ListStatus } from '../api/useListQrCodes';
import { DEFAULT_CUSTOMIZATION, type QrCodeRecord, type QrCodeType } from '../api/types';
import { QrPreview } from './QrPreview';

interface Props {
  type: QrCodeType;
  onCreate: () => void;
  onSelect: (record: QrCodeRecord) => void;
}

const QR_BASE_URL =
  process.env.REACT_APP_QR_BASE_URL?.replace(/\/$/, '') ||
  'https://api.socialsparkapp.net/prod/qr';

const STATUSES_CAMPAIGN: { id: ListStatus; label: string }[] = [
  { id: 'active', label: 'Active' },
  { id: 'ended', label: 'Ended' },
  { id: 'archived', label: 'Archived' },
];

const STATUSES_PERMANENT: { id: ListStatus; label: string }[] = [
  { id: 'active', label: 'Active' },
  { id: 'archived', label: 'Archived' },
];

export function QrCodeList({ type, onCreate, onSelect }: Props) {
  const isCampaign = type === 'campaign';
  const [status, setStatus] = useState<ListStatus>('active');
  const [search, setSearch] = useState('');

  const { data, loading, error, refetch } = useListQrCodes(type, status);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data;
    return data.filter(
      (r) =>
        r.campaign_id.toLowerCase().includes(q) ||
        (r.campaign_name || '').toLowerCase().includes(q),
    );
  }, [data, search]);

  const statuses = isCampaign ? STATUSES_CAMPAIGN : STATUSES_PERMANENT;

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-xl font-semibold text-white">
          {isCampaign ? 'Campaigns' : 'Permanent QRs'}
        </h2>
        <div className="flex gap-2 items-center flex-wrap">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={isCampaign ? 'Search campaigns…' : 'Search permanent QRs…'}
            className="bg-gray-900 border border-gray-800 rounded-md px-3 py-1.5 text-sm text-gray-100 placeholder:text-gray-600 focus:outline-none focus:border-violet-500 w-64"
          />
          <div className="flex gap-1">
            {statuses.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setStatus(s.id)}
                className={[
                  'px-3 py-1.5 text-xs rounded-md border transition-colors',
                  status === s.id
                    ? 'bg-violet-950/40 border-violet-500 text-violet-200'
                    : 'bg-gray-900 border-gray-800 text-gray-400 hover:border-gray-700 hover:text-gray-200',
                ].join(' ')}
              >
                {s.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={onCreate}
            className="px-3 py-1.5 text-sm rounded-md bg-violet-600 text-white font-medium hover:bg-violet-500"
          >
            {isCampaign ? '+ Create campaign' : '+ Create permanent QR'}
          </button>
        </div>
      </header>

      {error && (
        <div className="rounded-md border border-red-900/50 bg-red-950/30 px-3 py-2 text-sm text-red-300 flex items-center justify-between">
          <span>{error}</span>
          <button
            type="button"
            onClick={refetch}
            className="text-red-300/80 hover:text-red-200 underline text-xs"
          >
            Retry
          </button>
        </div>
      )}

      <div className="rounded-lg border border-gray-800 bg-gray-900/40 overflow-hidden">
        <div className={headerRow(isCampaign)}>
          <div></div>
          <div>{isCampaign ? 'Campaign' : 'Permanent QR'}</div>
          <div>Slug</div>
          {isCampaign && <div>Dates</div>}
          <div>Scans</div>
          <div>Last scanned</div>
        </div>

        {loading ? (
          <div className="px-4 py-12 text-center text-sm text-gray-500">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="px-4 py-12 text-center">
            <div className="text-sm text-gray-300 mb-1">
              {search
                ? 'No matches for that search.'
                : status === 'archived'
                ? `No archived ${isCampaign ? 'campaigns' : 'permanent QRs'} yet.`
                : status === 'ended'
                ? 'No ended campaigns yet.'
                : `No ${isCampaign ? 'campaigns' : 'permanent QRs'} yet.`}
            </div>
            {!search && status === 'active' && (
              <div className="text-xs text-gray-500">
                Use the “{isCampaign ? '+ Create campaign' : '+ Create permanent QR'}” button to create one.
              </div>
            )}
          </div>
        ) : (
          filtered.map((row) => (
            <button
              key={row.campaign_id}
              type="button"
              onClick={() => onSelect(row)}
              className={[
                'w-full text-left grid items-center px-4 py-3 border-t border-gray-800 hover:bg-gray-800/40 transition-colors',
                rowGridClass(isCampaign),
              ].join(' ')}
            >
              <div className="bg-white rounded-md p-1 w-10 h-10 flex items-center justify-center overflow-hidden">
                <QrPreview
                  data={`${QR_BASE_URL}/${row.campaign_id}`}
                  customization={row.customization || DEFAULT_CUSTOMIZATION}
                  logoUrl={row.logo_url}
                  size={32}
                />
              </div>
              <div className="text-sm text-gray-100 truncate">{row.campaign_name || row.campaign_id}</div>
              <div className="text-xs font-mono text-gray-500 truncate">{row.campaign_id}</div>
              {isCampaign && (
                <div className="text-xs text-gray-400">
                  {formatDateRange(row.start_date, row.end_date)}
                </div>
              )}
              <div className="text-sm font-semibold text-gray-100">
                {(row.scan_count ?? 0).toLocaleString()}
              </div>
              <div className="text-xs text-gray-400">
                {row.last_scanned_at ? formatRelative(row.last_scanned_at) : '—'}
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
}

function headerRow(isCampaign: boolean): string {
  return [
    'grid items-center px-4 py-2.5 bg-gray-900 text-[11px] uppercase tracking-wider text-gray-500',
    rowGridClass(isCampaign),
  ].join(' ');
}

function rowGridClass(isCampaign: boolean): string {
  return isCampaign
    ? 'grid-cols-[48px_2fr_1fr_1fr_80px_120px] gap-3'
    : 'grid-cols-[48px_2fr_1fr_80px_120px] gap-3';
}

function formatDateRange(start?: string, end?: string): string {
  if (!start && !end) return '—';
  const s = start ? formatShortDate(start) : '?';
  const e = end ? formatShortDate(end) : '?';
  return `${s} → ${e}`;
}

function formatShortDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function formatRelative(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const diffSec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diffSec < 60) return 'just now';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  if (diffSec < 7 * 86400) return `${Math.floor(diffSec / 86400)}d ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}
